import { type IDBPDatabase, openDB } from 'idb';

type ImageCacheEntry = {
  url: string;
  blob: Blob;
  size: number;
  createdAt: number;
  lastAccess: number;
};

const DB_NAME = 'imgenhancer-image-cache';
const STORE_NAME = 'images';
const MAX_CACHE_ITEMS = 300;
const MAX_CACHE_BYTES = 500 * 1024 * 1024;
const PREFETCH_CONCURRENCY = 3;
const PRUNE_WRITE_THRESHOLD = 20;
const PRUNE_DELAY_MS = 2_000;

let dbPromise: Promise<IDBPDatabase> | null = null;
const inFlight = new Map<string, Promise<Blob | null>>();
let activePrefetchCount = 0;
let writesSinceLastPrune = 0;
let pruneTimer: ReturnType<typeof setTimeout> | null = null;

type PrefetchPriority = 'high' | 'low';
type PrefetchQueueItem = {
  url: string;
  resolve: (blob: Blob | null) => void;
};

const highPriorityQueue: PrefetchQueueItem[] = [];
const lowPriorityQueue: PrefetchQueueItem[] = [];

const isBrowser = () =>
  typeof window !== 'undefined' && typeof indexedDB !== 'undefined';

async function getDb() {
  if (!isBrowser()) return null;
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    });
  }
  return dbPromise;
}

async function pruneCache(db: IDBPDatabase) {
  const entries = (await db.getAll(STORE_NAME)) as ImageCacheEntry[];
  if (!entries.length) return;
  let totalBytes = entries.reduce((sum, entry) => sum + (entry.size || 0), 0);
  if (entries.length <= MAX_CACHE_ITEMS && totalBytes <= MAX_CACHE_BYTES) {
    return;
  }
  const sorted = entries.sort((a, b) => a.lastAccess - b.lastAccess);
  let remainingCount = entries.length;
  for (const entry of sorted) {
    await db.delete(STORE_NAME, entry.url);
    remainingCount -= 1;
    totalBytes -= entry.size || 0;
    if (remainingCount <= MAX_CACHE_ITEMS && totalBytes <= MAX_CACHE_BYTES) {
      break;
    }
  }
}

function schedulePrune() {
  if (!isBrowser()) return;
  writesSinceLastPrune += 1;
  const shouldPruneNow = writesSinceLastPrune >= PRUNE_WRITE_THRESHOLD;
  if (pruneTimer) {
    if (!shouldPruneNow) {
      return;
    }
    clearTimeout(pruneTimer);
    pruneTimer = null;
  }
  pruneTimer = setTimeout(
    () => {
      pruneTimer = null;
      writesSinceLastPrune = 0;
      void (async () => {
        try {
          const db = await getDb();
          if (!db) return;
          await pruneCache(db);
        } catch {
          // Prune failures should not break cache reads/writes.
        }
      })();
    },
    shouldPruneNow ? 0 : PRUNE_DELAY_MS
  );
}

async function fetchAndCacheImage(url: string) {
  try {
    const cached = await getCachedImage(url);
    if (cached?.blob) {
      return cached.blob;
    }
    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    await setCachedImage(url, blob);
    return blob;
  } catch {
    return null;
  }
}

function dequeuePrefetchTask(): PrefetchQueueItem | undefined {
  return highPriorityQueue.shift() ?? lowPriorityQueue.shift();
}

function pumpPrefetchQueue() {
  if (!isBrowser()) return;
  while (activePrefetchCount < PREFETCH_CONCURRENCY) {
    const task = dequeuePrefetchTask();
    if (!task) {
      return;
    }
    activePrefetchCount += 1;
    void (async () => {
      const result = await fetchAndCacheImage(task.url);
      task.resolve(result);
    })().finally(() => {
      activePrefetchCount -= 1;
      inFlight.delete(task.url);
      pumpPrefetchQueue();
    });
  }
}

export async function getCachedImage(url: string) {
  const db = await getDb();
  if (!db) return null;
  const entry = (await db.get(STORE_NAME, url)) as ImageCacheEntry | undefined;
  if (!entry) return null;
  entry.lastAccess = Date.now();
  await db.put(STORE_NAME, entry, url);
  return entry;
}

export async function setCachedImage(url: string, blob: Blob) {
  const db = await getDb();
  if (!db) return;
  const now = Date.now();
  const entry: ImageCacheEntry = {
    url,
    blob,
    size: blob.size ?? 0,
    createdAt: now,
    lastAccess: now,
  };
  await db.put(STORE_NAME, entry, url);
  schedulePrune();
}

export async function deleteCachedImage(url: string) {
  const db = await getDb();
  if (!db) return;
  await db.delete(STORE_NAME, url);
}

export async function prefetchImageToCache(url: string) {
  return queuePrefetchImageToCache(url, { priority: 'low' });
}

export function queuePrefetchImageToCache(
  url: string,
  options: { priority?: PrefetchPriority } = {}
) {
  if (!isBrowser()) {
    return Promise.resolve(null);
  }
  if (!url) {
    return Promise.resolve(null);
  }
  const existingTask = inFlight.get(url);
  if (existingTask) {
    return existingTask;
  }
  const priority = options.priority ?? 'low';
  const task = new Promise<Blob | null>((resolve) => {
    const queue = priority === 'high' ? highPriorityQueue : lowPriorityQueue;
    queue.push({ url, resolve });
  });
  inFlight.set(url, task);
  pumpPrefetchQueue();
  return task;
}
